import * as React from "react";
import { PlayerPost, socialNetworkNames } from "@/interfaces";
import { PostServices } from "@/services";
import { PostServices as BedrockPostServices } from "@/services/bedrock";

import { PostErrorCell } from "./components/PostErrorCell";
import { modalReducer } from "./modalReducer";
import { Button, Dialog } from "@/components/ui";
import { toast } from "sonner";

interface OwnProps {
  opened: boolean;
  selectedPost: PlayerPost;
  thumbnail: string;
  closeHandler: () => void;
}

type Props = OwnProps;

/**
 * PostFailedModal Components Refactored to Tailwind & Shadcn
 */
const PostFailedModalFC: React.FC<Props> = ({ opened, selectedPost, thumbnail, closeHandler }) => {
  const [state, dispatch] = React.useReducer(modalReducer, { open: opened, loading: false });

  const dismissAlert = (): void => {
    dispatch({ type: "loading" });
    PostServices.dismissPost(selectedPost.postId)
      .then(close)
      .catch(e => {
        alert(e.message);
      })
      .finally(() => {
        dispatch({ type: "loaded" });
      });
  };

  const repost = (): void => {
    dispatch({ type: "loading" });
    BedrockPostServices.retryAll(selectedPost.postId)
      .then(() => toast.success("Post has been successfully re-scheduled. Check your calendar for updates"))
      .finally(() => dispatch({ type: "loaded" }))
      .catch(e => toast.error("There was a problem retrying this post", { description: e.message || "Unknown error" }))
      .then(close);
  };

  const decorated = selectedPost.socialNetworks.map(sn => {
    return { ...sn, createdDate: selectedPost.createdDate };
  });

  const failed = selectedPost.socialNetworks.filter(sn => sn.status === "FAILED");

  const close = () => {
    dispatch({ type: "close" });
    closeHandler();
  };

  return (
    <Dialog
      open={state.open}
      onOpenChange={open => !open && close()}
      size="md"
      showCloseButton
      key={selectedPost.postId}
    >
      <Dialog.Header>
        <Dialog.Title className="text-slate-800 font-bold">Failed Post</Dialog.Title>
      </Dialog.Header>

      <Dialog.Content className="flex flex-col gap-5 max-h-[70vh] overflow-y-auto overflow-x-hidden pr-1">
        {/* Upper Content Box */}
        <div className="flex flex-row gap-5 items-start justify-between w-full min-w-0">
          <div className="flex flex-col justify-between flex-1 min-w-0 min-h-[160px] text-left">
            <p className="text-slate-800 text-sm font-medium leading-relaxed whitespace-pre-wrap line-clamp-[6] max-h-[12em] overflow-hidden">
              {selectedPost.message}
            </p>
          </div>
          {thumbnail && (
            <div className="w-36 h-48 shrink-0 rounded-xl overflow-hidden border border-slate-200 shadow-sm bg-slate-50">
              <img src={thumbnail} alt="Post preview" className="size-full object-cover" />
            </div>
          )}
        </div>

        {/* Post Statuses list */}
        <div className="flex flex-col gap-2 pt-4 border-t border-slate-100 text-left">
          <h5 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Post Status</h5>
          <div className="flex flex-col w-full">
            {decorated.map((item, index) => (
              <PostErrorCell socialNetwork={item} key={`${index}_${item.code}_${selectedPost.postId}`} />
            ))}
          </div>
        </div>
      </Dialog.Content>

      <Dialog.Footer className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-between w-full border-t border-t-border bg-gray-50 rounded-b-xl p-4">
        <Button variant="outline" disabled={state.loading} onClick={dismissAlert}>
          Dismiss Alert
        </Button>

        <Button variant="default" disabled={state.loading} onClick={repost}>
          {failed.length === 1
            ? `Re-Post to ${socialNetworkNames[failed[0].socialNetworkId!!]}`
            : "Re-Post to Failed Networks"}
        </Button>
      </Dialog.Footer>
    </Dialog>
  );
};

export const PostFailedModal = PostFailedModalFC;
